import _ from "lodash";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import styled from "styled-components";
import CuideoSearchField from "../../../../../../components/CuideoFields/CuideoSearchField";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import useDebounce from "../../../../../../util/useDebounce";
import FilterButton from "../../../../components/FilterButton";
import FiltersButton, {
  Filter,
} from "../../../../components/FiltersButton/FiltersButton";
import {
  resetState,
  TServicesPageFilters,
} from "../../hooks/useServicesPageFilters";
import { TStaffUserResponse } from "../../../../../../api/user/apiListStaffUsers";
import { USER_ROLES } from "../../../../../../api/security";

const StyledWrapper = styled.div`
  background-color: ${cuideoTheme.palette.common.white};
  padding: 0.5rem 0 0.5rem 4rem;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 2px 4px 0 rgba(0, 72, 120, 0.25);
`;

const StyledContainer = styled.div`
  margin: 0 1rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledButtonWrapper = styled.div`
  margin: 0 0 0 0.5rem;
  .MuiButtonBase-root {
    line-height: 1;
  }
`;

const StyledLeft = styled.div``;

const StyledRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

interface IServicesPageFiltersProps {
  servicesPageFilters: TServicesPageFilters;
  servicesPageSetFilters: (filters: TServicesPageFilters) => void;
}

const ServicesPageFilters = (props: IServicesPageFiltersProps) => {
  const { servicesPageFilters, servicesPageSetFilters } = props;
  const [searchTerm, setSearchTerm] = useState(servicesPageFilters.search);
  const [staff, setStaff] = useState<any>([]);
  const [commercialStaff, setCommercialStaff] = useState<any>([]);
  const staffState = useSelector((state: any) => state.usuariosState.staff);

  const { formatMessage } = useIntl();

  const handleApply = (filterName: string, value: any) => {
    const newFilters = { ...servicesPageFilters, [filterName]: value, page: 1 };
    if (!_.isEqual(servicesPageFilters, newFilters)) {
      servicesPageSetFilters(newFilters);
    }
  };

  const handleReset = (filterName: string) => {
    const newFilters = { ...servicesPageFilters, [filterName]: "", page: 1 };
    if (!_.isEqual(servicesPageFilters, newFilters)) {
      servicesPageSetFilters(newFilters);
    }
  };

  const handleSearchChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    const newStaffOptions = staffState
      .filter(
        (s: TStaffUserResponse) =>
          s.numOwnedContracts > 0 ||
          s.numOwnedServices > 0 ||
          s.roles.indexOf(USER_ROLES.ROLE_ES_RECRUITER) !== -1 ||
          s.roles.indexOf(USER_ROLES.ROLE_ES_PROMOTER) !== -1
      )
      .map((s: TStaffUserResponse) => {
        return {
          value: s.id,
          label: s.fullname,
        };
      });
    const sortedStaffOptions = newStaffOptions.sort(
      (
        a: { value: string; label: string },
        b: { value: string; label: string }
      ) => {
        if (a.label < b.label) {
          return -1;
        }
        return 1;
      }
    );
    setStaff(sortedStaffOptions);
  }, [staffState, setStaff]);

  useEffect(() => {
    const newCommercialStaffOptions = staffState
      .filter((s: TStaffUserResponse) => s.numOwnedDeals > 0)
      .map((s: TStaffUserResponse) => {
        return {
          value: s.id,
          label: s.fullname,
        };
      });
    const sortedCommercialStaffOptions = newCommercialStaffOptions.sort(
      (
        a: { value: string; label: string },
        b: { value: string; label: string }
      ) => {
        if (a.label < b.label) {
          return -1;
        }
        return 1;
      }
    );
    setCommercialStaff(sortedCommercialStaffOptions);
  }, [staffState, setCommercialStaff]);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const newFilters = { ...servicesPageFilters, search: debouncedSearchTerm };
    if (!_.isEqual(servicesPageFilters, newFilters)) {
      servicesPageSetFilters({ ...newFilters, page: 1 });
    }
  }, [servicesPageFilters, servicesPageSetFilters, debouncedSearchTerm]);

  const handleFiltersButtonChange = (filtersValues: any) => {
    const newFilters = {
      ...servicesPageFilters,
      // state: filtersValues.state.isActive ? filtersValues.state.value : "",
      owner: filtersValues.owner.isActive ? filtersValues.owner.value : [],
      cOwner: filtersValues.cOwner.isActive ? filtersValues.cOwner.value : [],
      page: 1,
    };
    if (!_.isEqual(servicesPageFilters, newFilters)) {
      servicesPageSetFilters(newFilters);
    }
  };

  const staffOptions = staff;

  const filters = [] as Filter[];
  filters.push({
    name: "owner",
    filterLabel: formatMessage({
      id: "ServicePageFilters.Owner",
      defaultMessage: "Propietario",
    }),
    type: "multi",
    options: [...staffOptions],
  });
  filters.push({
    name: "cOwner",
    filterLabel: formatMessage({
      id: "ServicePageFilters.Comercial",
      defaultMessage: "Comercial",
    }),
    type: "multi",
    options: [...commercialStaff],
  });

  return (
    <StyledWrapper>
      <StyledContainer>
        <StyledLeft>
          <CuideoSearchField
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={formatMessage({
              id: "ServicesPageFilters.Search",
              defaultMessage: "Buscar",
            })}
          />
        </StyledLeft>
        <StyledRight>
          <StyledButtonWrapper>
            <FilterButton
              defaultButtonText={
                <FormattedMessage
                  id="ServicePagesFilters.Country"
                  defaultMessage="País"
                />
              }
              options={[
                {
                  value: "es",
                  label: formatMessage({
                    id: "ServicePagesFilters.Country.es",
                    defaultMessage: "España",
                  }),
                },
                {
                  value: "fr",
                  label: formatMessage({
                    id: "ServicePagesFilters.Country.fr",
                    defaultMessage: "Francia",
                  }),
                },
              ]}
              initialValue={servicesPageFilters.businessCountry}
              handleApply={handleApply}
              handleReset={handleReset}
              filterName="businessCountry"
              inUse={servicesPageFilters.businessCountry !== ""}
              resetOptions={"es"}
            />
          </StyledButtonWrapper>
          <StyledButtonWrapper>
            <FiltersButton
              filters={filters}
              filtersValues={{
                // state: {
                //   value: servicesPageFilters.state,
                // },
                owner: {
                  value: servicesPageFilters.owner,
                },
                cOwner: {
                  value: servicesPageFilters.cOwner,
                },
              }}
              resetOptions={{
                // state: {
                //   value: resetState.state,
                // },
                owner: {
                  value: resetState.owner,
                },
                cOwner: {
                  value: resetState.cOwner,
                },
              }}
              onFiltersChange={handleFiltersButtonChange}
            />
          </StyledButtonWrapper>
        </StyledRight>
      </StyledContainer>
    </StyledWrapper>
  );
};

export default ServicesPageFilters;
